import { useCallback, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { UseMultiDaySyncResult } from './use-multi-day-sync.types';
import {
  getDayOneTemplateBlock,
  syncBlocksWithDates,
} from '../helpers/multi-day-day-block.helpers';
import { MULTI_DAY_WORK_FORM_FIELDS, MultiDayWorkFormContext } from '../multi-day-work.types';

export const useMultiDaySync = (): UseMultiDaySyncResult => {
  const { clearErrors, getValues, setValue } = useFormContext<MultiDayWorkFormContext>();

  const syncDates = useCallback(
    (nextDates: string[]): boolean => {
      const existingBlocks = getValues(MULTI_DAY_WORK_FORM_FIELDS.multiDayDayBlocks) ?? [];
      const templateBlock = getDayOneTemplateBlock(existingBlocks);
      const syncedBlocks = syncBlocksWithDates(nextDates, existingBlocks, templateBlock);

      setValue(MULTI_DAY_WORK_FORM_FIELDS.multiDayDates, nextDates);
      setValue(MULTI_DAY_WORK_FORM_FIELDS.multiDayDayBlocks, syncedBlocks);
      clearErrors(MULTI_DAY_WORK_FORM_FIELDS.multiDayDates);
      clearErrors(MULTI_DAY_WORK_FORM_FIELDS.multiDayDayBlocks);

      return true;
    },
    [clearErrors, getValues, setValue]
  );

  const deleteBlockAtIndex = useCallback(
    (index: number): boolean => {
      const blocks = getValues(MULTI_DAY_WORK_FORM_FIELDS.multiDayDayBlocks) ?? [];

      const blockToRemove = blocks[index];

      if (!blockToRemove) {
        return false;
      }

      const nextBlocks = blocks.filter((_, blockIndex) => blockIndex !== index);
      const currentDates = getValues(MULTI_DAY_WORK_FORM_FIELDS.multiDayDates) ?? [];
      const nextDates = currentDates.filter((date) => date !== blockToRemove.date);

      setValue(MULTI_DAY_WORK_FORM_FIELDS.multiDayDates, nextDates);
      setValue(MULTI_DAY_WORK_FORM_FIELDS.multiDayDayBlocks, nextBlocks);
      clearErrors(MULTI_DAY_WORK_FORM_FIELDS.multiDayDates);
      clearErrors(MULTI_DAY_WORK_FORM_FIELDS.multiDayDayBlocks);

      return true;
    },
    [clearErrors, getValues, setValue]
  );

  return {
    deleteBlockAtIndex,
    syncDates,
  };
};

export const useMultiDayAutoExpandNewBlock = (expandBlock: (index: number) => void): void => {
  const { getValues, watch } = useFormContext<MultiDayWorkFormContext>();
  const dates = watch(MULTI_DAY_WORK_FORM_FIELDS.multiDayDates) ?? [];
  const previousDatesRef = useRef(dates);

  useEffect(() => {
    const previousDates = previousDatesRef.current;
    const addedDates = dates.filter((date) => !previousDates.includes(date));

    if (addedDates.length > 0) {
      const blocks = getValues(MULTI_DAY_WORK_FORM_FIELDS.multiDayDayBlocks) ?? [];
      const addedDate = addedDates[addedDates.length - 1];
      const addedBlockIndex = blocks.findIndex((block) => block.date === addedDate);

      if (addedBlockIndex >= 0) {
        expandBlock(addedBlockIndex);
      }
    }

    previousDatesRef.current = dates;
  }, [dates, expandBlock, getValues]);
};
